import React, { useCallback, useEffect, useRef, useState } from "react";
import Header from "../../Core/LayoutV2/Header";
import RequestHeader from "./RequestHeader";
import ChatHeader from "./ChatHeader";
import ConversationInput from "./ConversationInput";
import Message, { Day, TypingIndicator, Unread } from "./Message";
import { useAppDispatch, useAppSelector } from "../../Core/redux/hooks";
import {
  updateConversations,
  updateReaction,
  deleteLocalMessage,
  removeAttachmentFromMessage,
  updateAllChatLastMessage,
  updateAllChatUnreadCount,
  updateUnreadCountForMarkAsUnread,
  updateUnreadCountForProjectThreads,
  addMessage,
  evictChatMessages,
} from "../../Core/redux/slices/conversations.slice";
import { updateDashboard } from "../../Core/redux/slices/dashboard.slice";
import { playNotifySoundDebounced } from "../../Core/utils";
import dayjs from "dayjs";
import {
  getThreadChat,
  deleteMessage as deleteMessageAPI,
  updateMessagesReceivedAPI,
  updateMessagesReadAPI,
  getCommonThreads as getCommonThreadsAPI,
} from "../../Core/redux/api/v3/conversationAPI";
import { updateMessageReceived, updateMessageRead } from "../../Core/redux/slices/conversations.slice";
import { MESSAGES_READ_STATUS_EVENT, MESSAGE_RECEIVED_STATUS_EVENT } from "../../Core/utils/socketConstants";
import ChatEmptyState from "./ChatEmptyState";
import { chatClient } from "../../Core/utils/axiosClient";
import DragAndDropLayer from "./ConversationInput/DragAndDropLayer";
import { updateVault } from "../../Core/redux/slices/vault.slice";
import { useLocation, useParams } from "react-router-dom";
import QuickChatMessage from "./QuickChatMessage";
import usePermission from "../../Core/hooks/usePermission";
import MessageInfoDrawer from "./Drawers/MessageInfoDrawer";
import DeleteMessageModal from "./Modals/DeleteMessageModal";
import MessageDeletedToast from "./Modals/MessageDeletedToast";
import { addReaction } from "../../Core/redux/api/v3/conversationAPI";
import {
  starMessageAPI, unStarMessageAPI, pinMessageAPI,
  getPinnedMessageAPI
} from "../../Core/redux/api/v3/conversationAPI";
import {
  starMessageLocally, setPinnedMessage,
  clearPinnedMessage,
  setCommonThreads,
} from "../../Core/redux/slices/conversations.slice";
import { MESSAGE_STARRED_EVENT } from "../../Core/utils/socketConstants";
import PinnedMessageBanner from "./PinnedMessageBanner";
import { getThreadMediaAPI } from "../../Core/redux/api/v3/conversationAPI";
import { setThreadMedia } from "../../Core/redux/slices/conversations.slice";

const ChatWindow = ({ socket, comments = false, view = "" }) => {
  const { currentThread, unreadCount: unreadCountFromRedux, getMessageLoading } =
    useAppSelector((state) => state.conversations);
  const { tenantDetails } = useAppSelector((state) => state.tenant);
  const { openCommentsModal } = useAppSelector((state) => state.vault);
  const { user } = useAppSelector((state) => state.userDetails);
  const dispatch = useAppDispatch();
  const [messageDeletedToast, setMessageDeletedToast] = useState(false);

  const chatId = currentThread?.threadId;
  const chatIdStr = chatId != null && chatId !== "" ? String(chatId) : null;

  const chatMessages = useAppSelector((state) =>
    chatIdStr ? state.conversations.allChatMessages[chatIdStr] : undefined
  ) ?? [];
  const chatPagination = useAppSelector((state) =>
    chatIdStr ? state.conversations.chatPagination?.[chatIdStr] : undefined
  );
  const chatCacheMeta = useAppSelector((state) =>
    chatIdStr ? state.conversations.chatCacheMeta?.[chatIdStr] : undefined
  );
  const { threadMembers } = useAppSelector((state) => state.conversations);
  const pinnedMessage = useAppSelector((state) =>
    chatIdStr ? state.conversations.pinnedMessages?.[chatIdStr] ?? null : null
  );
  const [pinActionToast, setPinActionToast] = useState<{ visible: boolean; action: "pinned" | "unpinned" }>({
    visible: false,
    action: "pinned",
  });
  const pendingPinScrollRef = useRef<string | null>(null);

  useEffect(() => {
    if (!pinActionToast.visible) return;
    const t = setTimeout(() => setPinActionToast((s) => ({ ...s, visible: false })), 2500);
    return () => clearTimeout(t);
  }, [pinActionToast.visible]);

  const [forbidScrollToBottom, setForbidScrollToBottom] = useState(false);
  const [prevScrollHeight, setPrevScrollHeight] = useState<number | null>(null);
  const [deleteMessageModal, setDeleteMessageModal] = useState({ visible: false, id: null });

  const chatWindow = React.useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const isLoadingRef = useRef(false);

  useEffect(() => {
    isLoadingRef.current = !!chatPagination?.loading;
  }, [chatPagination?.loading]);

  // ── Initial fetch / thread change ────────────────────────────────────────
  useEffect(() => {
    if (!chatIdStr) return;
    document.getElementById("conversation-chat-input")?.focus();

    dispatch(updateConversations({ key: "currentChatId", value: chatIdStr }));

    const hasCached = (chatMessages?.length ?? 0) > 0;
    const shouldFetch =
      !hasCached ||
      chatCacheMeta?.source === "evicted" ||
      !chatCacheMeta?.lastServerFetch;

    dispatch(getThreadChat({ chatId, lastMessage: undefined }));
    setForbidScrollToBottom(false);

    dispatch(updateAllChatUnreadCount({ chatId: chatIdStr, value: 0, reset: true }));
    getThreadMediaAPI(chatId)
      .then((res) => {
        if (!res) return;
        dispatch(setThreadMedia({ chatId: chatIdStr, media: res.media, documents: res.documents }));
      })
      .catch((e) => console.error("getThreadMediaAPI failed:", e));
    dispatch(getPinnedMessageAPI({ threadId: chatId })).then((result: any) => {
      if (result?.payload?.status === 1 && result.payload.pinnedMessage) {
        dispatch(setPinnedMessage({ chatId: chatIdStr, pinnedMessage: result.payload.pinnedMessage }));
      } else {
        dispatch(clearPinnedMessage({ chatId: chatIdStr }));
      }
    });

    // Prefetch "Groups in common" here (private threads only) so
    // UserDetailsDrawer never has to make its own API call — it just reads
    // conversations.commonThreads[otherUserId] from Redux, so opening the
    // drawer has zero lag regardless of how long this call takes.
    const otherUserIdForCommon = currentThread?.userId ?? currentThread?.userid;
    if (currentThread?.type === "private" && otherUserIdForCommon) {
      getCommonThreadsAPI({ otherUserId: otherUserIdForCommon })
        .then((res) => {
          dispatch(setCommonThreads({ otherUserId: otherUserIdForCommon, groups: res || [] }));
        })
        .catch((e) => {
          console.error("getCommonThreadsAPI failed:", e);
          dispatch(setCommonThreads({ otherUserId: otherUserIdForCommon, groups: [] }));
        });
    }

    updateMessagesReadAPI({ chatId: chatIdStr, userId: user.id, user });
    dispatch(updateMessageRead({ chatId: chatIdStr, userId: user.id, user }));
    socket.emit(MESSAGES_READ_STATUS_EVENT, { chatId: chatIdStr, userId: user.id });

    return () => {
      dispatch(evictChatMessages({ chatId }));
      dispatch(updateConversations({ key: "currentChatId", value: null }));
    };
  }, [chatId]);

  useEffect(() => {
    if (!pinnedMessage?.pinnedUntil || !chatIdStr) return;
    const remaining = new Date(pinnedMessage.pinnedUntil).getTime() - Date.now();

    if (remaining <= 0) {
      dispatch(clearPinnedMessage({ chatId: chatIdStr }));
      return;
    }
    if (remaining <= 24 * 60 * 60 * 1000) {
      const timer = setTimeout(() => dispatch(clearPinnedMessage({ chatId: chatIdStr })), remaining);
      return () => clearTimeout(timer);
    }
  }, [pinnedMessage?.pinnedUntil, chatIdStr, dispatch]);


  useEffect(() => {
    if (!chatIdStr) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;

      dispatch(updateAllChatUnreadCount({ chatId: chatIdStr, value: 0, reset: true }));
      dispatch(updateMessageRead({ chatId: chatIdStr, userId: user.id, user }));
      updateMessagesReadAPI({ chatId: chatIdStr, userId: user.id, user });
      socket.emit(MESSAGES_READ_STATUS_EVENT, { chatId: chatIdStr, userId: user.id });
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [chatIdStr, user, socket, dispatch]);

  const scrollToChatBottom = (behavior: ScrollBehavior = "auto") => {
    messagesEndRef.current?.scrollIntoView({ block: "end", behavior });
  };

  useEffect(() => {
    if (!forbidScrollToBottom) {
      scrollToChatBottom();
    } else {
      setForbidScrollToBottom(false);
    }
  }, [chatId, chatMessages?.length]);

  useEffect(() => {
    const container = chatWindow.current;
    if (!container) return;

    const ro = new ResizeObserver(() => {
      const distanceFromBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight;
      if (distanceFromBottom < 150) {
        scrollToChatBottom();
      }
    });

    ro.observe(container);
    const content = container.firstElementChild;
    if (content) ro.observe(content);

    return () => ro.disconnect();
  }, [chatId]);

  // ── Load older messages on scroll-to-top ─────────────────────────────────
  const triggerLoadMore = useCallback(() => {
    if (!chatIdStr) return;
    if (isLoadingRef.current) return;
    if (!chatPagination?.hasMore) return;
    if (!chatPagination?.oldestMessageDate) return;

    isLoadingRef.current = true;
    setForbidScrollToBottom(true);
    setPrevScrollHeight(chatWindow.current?.scrollHeight ?? null);
    dispatch(getThreadChat({ chatId, lastMessage: chatPagination.oldestMessageDate }));
  }, [chatIdStr, chatPagination, chatId, dispatch]);

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      if (e.currentTarget.scrollTop < 150) {
        triggerLoadMore();
      }
    },
    [triggerLoadMore]
  );

  const handleBannerPress = useCallback(() => {
    if (!pinnedMessage?._id) return;
    const el = chatWindow.current?.querySelector(
      `[data-message-id="${pinnedMessage._id}"]`
    ) as HTMLElement | null;

    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("bg-primary-50/60");
      setTimeout(() => el.classList.remove("bg-primary-50/60"), 1200);
      return;
    }

    pendingPinScrollRef.current = pinnedMessage._id;
    if (chatPagination?.hasMore && !isLoadingRef.current) {
      isLoadingRef.current = true;
      dispatch(getThreadChat({ chatId, lastMessage: chatPagination.oldestMessageDate }));
    }
  }, [pinnedMessage, chatPagination, chatId, dispatch]);

  const prevPinLoadingRef = useRef(false);
  useEffect(() => {
    const wasLoading = prevPinLoadingRef.current;
    const isNowDone = !chatPagination?.loading;
    prevPinLoadingRef.current = !!chatPagination?.loading;

    if (!wasLoading || !isNowDone || !pendingPinScrollRef.current) return;

    const targetId = pendingPinScrollRef.current;
    const el = chatWindow.current?.querySelector(`[data-message-id="${targetId}"]`) as HTMLElement | null;

    if (el) {
      pendingPinScrollRef.current = null;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    } else if (chatPagination?.hasMore) {
      dispatch(getThreadChat({ chatId, lastMessage: chatPagination.oldestMessageDate }));
    } else {
      pendingPinScrollRef.current = null;
    }
  }, [chatPagination?.loading, chatPagination?.hasMore, chatPagination?.oldestMessageDate, chatId, dispatch]);

  useEffect(() => {
    if (prevScrollHeight != null && chatWindow.current) {
      const newHeight = chatWindow.current.scrollHeight;
      chatWindow.current.scrollTop = newHeight - prevScrollHeight;
      setPrevScrollHeight(null);
    }
  }, [chatMessages]);

  // ── Reactions ─────────────────────────────────────────────────────────────
  const reactToMessage = (emoji, messageId, isAlreadyReacted) => {
    if (comments) {
      addReaction({ messageId, userId: user.id, emoji });
      const updateComments = openCommentsModal.comments.map((m) => {
        if (m._id === messageId) {
          const reactions = m.reactions || {};
          const userReaction = reactions[emoji] ? reactions[emoji] : [];
          return { ...m, reactions: { ...reactions, [emoji]: [...userReaction, user.id] } };
        }
        return m;
      });
      dispatch(updateVault({ key: "openCommentsModal", value: { ...openCommentsModal, comments: updateComments } }));
      return;
    }

    addReaction({ messageId, userId: user.id, emoji });

    socket.emit("reaction", { emoji, userId: user.id, messageId, isAlreadyReacted, threadId: chatId, chatId: chatId });
    dispatch(updateReaction({ messageId, userId: user.id, emoji, threadId: chatId }));
  };

  // ── Star/Unstar ─────────────────────────────────────────────────────────────
  const handleStarUnStar = async (message, isStarred) => {
    const chatIdToUse = message.chat || message.threadId || chatId;

    dispatch(
      starMessageLocally({
        chatId: chatIdToUse,
        messageId: message._id,
        userId: user.id,
        starring: !isStarred,
      })
    );

    socket.emit(MESSAGE_STARRED_EVENT, {
      chatId: chatIdToUse,
      messageId: message._id,
      userId: user.id,
      starring: !isStarred,
    });

    try {
      if (isStarred) {
        await dispatch(unStarMessageAPI({ chatId: chatIdToUse, messageId: message._id, userId: user.id }));
      } else {
        await dispatch(starMessageAPI({ chatId: chatIdToUse, messageId: message._id, userId: user.id }));
      }
    } catch (error) {
      console.error("star/unstar error:", error);
      // Roll back on failure
      dispatch(
        starMessageLocally({
          chatId: chatIdToUse,
          messageId: message._id,
          userId: user.id,
          starring: isStarred,
        })
      );
    }
  };

  const handlePinMessage = async (message: any, duration: string) => {
    const chatIdToUse = message.chat || message.threadId || chatId;
    const chatKey = String(chatIdToUse);

    const optimisticPin = {
      _id: message._id,
      content: message.content || message.message,
      senderName: message.senderName,
      attachments: message.attachments,
      pinnedBy: user.id,
      pinnedUntil: null,
    };
    dispatch(setPinnedMessage({ chatId: chatKey, pinnedMessage: optimisticPin }));

    const resultAction = await dispatch(pinMessageAPI({ threadId: chatIdToUse, messageId: message._id, duration }));

    if (pinMessageAPI.rejected.match(resultAction)) {
      console.error("pin failed:", resultAction.payload);
      dispatch(clearPinnedMessage({ chatId: chatKey }));
      return;
    }

    const result: any = resultAction.payload;
    if (result?.status === 1) {
      dispatch(setPinnedMessage({ chatId: chatKey, pinnedMessage: { ...optimisticPin, pinnedUntil: result.pinnedUntil } }));

      socket.emit("pinMessage", {
        threadId: chatIdToUse,
        messageId: message._id,
        message: {
          content: optimisticPin.content,
          senderName: optimisticPin.senderName,
          attachments: optimisticPin.attachments,
        },
        pinnedBy: user.id,
        pinnedUntil: result.pinnedUntil,
      });

      setPinActionToast({ visible: true, action: "pinned" });
    }
  };

  const handleUnpinMessage = async (message: any) => {
    const chatIdToUse = message.chat || message.threadId || chatId;
    const chatKey = String(chatIdToUse);

    dispatch(clearPinnedMessage({ chatId: chatKey }));

    const resultAction = await dispatch(pinMessageAPI({ threadId: chatIdToUse, messageId: null, duration: null }));

    if (pinMessageAPI.rejected.match(resultAction)) {
      console.error("unpin error:", resultAction.payload);
      const refetch: any = await dispatch(getPinnedMessageAPI({ threadId: chatIdToUse }));
      if (refetch?.payload?.status === 1 && refetch.payload.pinnedMessage) {
        dispatch(setPinnedMessage({ chatId: chatKey, pinnedMessage: refetch.payload.pinnedMessage }));
      }
      return;
    }

    socket.emit("pinMessage", { threadId: chatIdToUse, messageId: null });
    setPinActionToast({ visible: true, action: "unpinned" });
  };

  const socketOnPinMessage = ({ threadId, messageId, message, pinnedBy, pinnedUntil }) => {
    const chatKey = String(threadId);
    if (!messageId) {
      dispatch(clearPinnedMessage({ chatId: chatKey }));
    } else {
      dispatch(setPinnedMessage({
        chatId: chatKey,
        pinnedMessage: {
          _id: messageId,
          content: message?.content,
          senderName: message?.senderName,
          attachments: message?.attachments,
          pinnedBy,
          pinnedUntil,
        },
      }));
    }
  };

  const socketOnReaction = ({ messageId, userId, emoji, threadId, chatId }) => {

    if (String(userId) === String(user.id)) return;

    const resolvedThreadId = threadId ?? chatId;
    dispatch(updateReaction({ messageId, userId, emoji, threadId: resolvedThreadId }));
  };

  const onMessageStarred = ({ chatId: starredChatId, messageId, userId, starring }) => {
    // Skip echo of our own action already applied optimistically
    if (String(userId) === String(user.id)) return;
    dispatch(starMessageLocally({ chatId: starredChatId, messageId, userId, starring }));
  };


  const [typingStatusState, setTypingStatusState] = useState({
    isTyping: false, userName: "", threadId: "", userId: "", threadType: "", message: "",
  });

  const socketOnTyping = ({ isTyping, threadId, userName, userId, threadType, tenantId, message }) => {
    if (tenantDetails.tenantId === tenantId) {
      if (isTyping) {
        setTypingStatusState({ isTyping, userName, threadId, userId, threadType, message });
      } else {
        setTimeout(() => setTypingStatusState({ isTyping, userName, threadId, userId, threadType, message }), 3000);
      }
      scrollToChatBottom();
    }
  };

  const socketOnAnyMessage = (message) => {
    dispatch(addMessage({ chatId: message.threadId ?? message.chat, message: { ...message, reactions: {} } }));

    if (chatIdStr && String(message.threadId ?? message.chat) === chatIdStr) {
      scrollToChatBottom();
      socket.emit("readMessageFromSocket", {
        threadId: chatId,
        threadType: currentThread.type,
        userId: user.id,
        reciverId: currentThread.userId,
      });

      updateMessagesReadAPI({ chatId: chatIdStr, userId: user.id, user });
      dispatch(updateMessageRead({ chatId: chatIdStr, userId: user.id, user }));
      socket.emit(MESSAGES_READ_STATUS_EVENT, { chatId: chatIdStr, userId: user.id });
    } else {
      playNotifySoundDebounced();

      const receivedChatId = message.threadId ?? message.chat;
      updateMessagesReceivedAPI({ chatId: receivedChatId, userId: user.id, user, message });
      dispatch(updateMessageReceived({ chatId: receivedChatId, message, userId: user.id }));
      socket.emit(MESSAGE_RECEIVED_STATUS_EVENT, { chatId: receivedChatId, message, userId: user.id });
    }
  };
  const socketOnMessageDeleted = ({
    messageId,
    chatId,
    deletedWhole,
    attachmentUrl,
    remainingAttachments,
    lastMessagePreview,
    lastMessageTime,
    unreadCountMap,
  }) => {
    const myId = String(user.id);

    if (deletedWhole === false && attachmentUrl) {
      dispatch(removeAttachmentFromMessage({
        chatId,
        messageId,
        attachmentUrl,
        remainingAttachments: remainingAttachments ?? [],
      }));
    } else {
      dispatch(deleteLocalMessage({
        chatId,
        message: { _id: messageId },
        lastMessageTime,
      }));
    }

    dispatch(updateAllChatLastMessage({ chatId, value: lastMessagePreview ?? "" }));

    if (unreadCountMap?.[myId] !== undefined) {
      dispatch(updateAllChatUnreadCount({ chatId, value: unreadCountMap[myId], reset: true }));
    }
  };

  React.useEffect(() => {
    socket.on("reaction", socketOnReaction);
    socket.on("userTyping", socketOnTyping);
    socket.on("privateMessage", socketOnAnyMessage);
    socket.on("message", socketOnAnyMessage);
    socket.on("messageDeleted", socketOnMessageDeleted);
    socket.on(MESSAGE_STARRED_EVENT, onMessageStarred);
    socket.on(MESSAGE_RECEIVED_STATUS_EVENT, (payload) => {
      dispatch(updateMessageReceived({ chatId: payload.chatId, message: payload.message, userId: payload.userId }));
    });
    socket.on(MESSAGES_READ_STATUS_EVENT, ({ chatId, userId }) => {
      dispatch(updateMessageRead({ chatId, userId }));
    });
    socket.on("pinMessage", socketOnPinMessage);
    return () => {
      socket.off("reaction", socketOnReaction);
      socket.off("userTyping", socketOnTyping);
      socket.off("privateMessage", socketOnAnyMessage);
      socket.off("message", socketOnAnyMessage);
      socket.off("messageDeleted", socketOnMessageDeleted);
      socket.off("pinMessage", socketOnPinMessage);
      socket.off(MESSAGE_RECEIVED_STATUS_EVENT);
      socket.off(MESSAGES_READ_STATUS_EVENT);
      socket.off(MESSAGE_STARRED_EVENT, onMessageStarred);
    };
  }, [socket, chatId]);

  const deleteMessage = (id) => {
    socket.emit("messageDeleted", { messageId: id, threadId: chatId });
    dispatch(deleteLocalMessage({ chatId, message: { _id: id } }));
    deleteMessageAPI({ chatId, messageId: id });

    dispatch(
      updateDashboard({
        key: "alertPopupModal",
        value: {
          visible: true,
          data: {
            title: "Message deleted",
            subtitle: "Message deleted successfully!",
            description: "",
          },
        },
      })
    );
  };

  const handleMarkAsUnread = async (message) => {
    const allMessages = chatMessages;
    const messageIndex = allMessages.findIndex((m) => m._id === message._id);
    const newUnreadCount = allMessages.length - messageIndex;

    dispatch(updateUnreadCountForMarkAsUnread({ threadId: chatId, unreadCount: newUnreadCount }));

    await chatClient.put("/conversation/unread-count-for-mark-as-unread", {
      threadId: chatId,
      senderId: user.id,
      receiverId: currentThread.userId,
      unreadCount: newUnreadCount,
    });
  };

  function groupedDays(messages) {
    return messages?.reduce((acc, el) => {
      const day = dayjs(el.createdAt).format("YYYY-MM-DD");
      return { ...acc, [day]: (acc[day] || []).concat([el]) };
    }, {});
  }

  function generateItems(messages: any[], unreadCount?: number) {
    const days = groupedDays(messages);
    const sortedDays = Object.keys(days).sort(
      (x, y) => dayjs(x, "YYYY-MM-DD").unix() - dayjs(y, "YYYY-MM-DD").unix()
    );
    const items = sortedDays.reduce((acc, date) => {
      const sortedMessages = [...days[date]].sort(
        (x, y) => new Date(x.createdAt).valueOf() - new Date(y.createdAt).valueOf()
      );
      return acc.concat([{ type: "day", date, id: `day-${date}` }, ...sortedMessages]);
    }, []);
    if (unreadCount && unreadCount > 0) {
      items.splice(items.length - unreadCount, 0, { type: "unread", count: unreadCount, id: "unread-divider" });
    }
    return items;
  }

  function renderItem(message, prevMessage) {

    if (message.type === "day") return <Day key={message.id} {...message} />;
    if (message.type === "unread") return <Unread key={message.id ?? "unread-divider"} {...message} />;

    let showSender = true;
    if (prevMessage && !prevMessage.type) {
      if (prevMessage.senderId === message.senderId) showSender = false;
      if (message.request?.type) showSender = true;
    }
    const participantsWithoutUser = (
      currentThread?.participants?.length
        ? currentThread.participants
        : (threadMembers || []).map((m) => m.id ?? m.userId)
    ).filter((id) => String(id) !== String(user.id));
    const Comp = view === "quickChat" ? QuickChatMessage : Message;
    return (
      <Comp
        key={message._id}
        message={message}
        reactToMessage={reactToMessage}
        onStarMessage={handleStarUnStar}
        onPinMessage={handlePinMessage}
        onUnpinMessage={handleUnpinMessage}
        deleteMessage={(id) => setDeleteMessageModal({ visible: true, id })}
        comments={comments}
        showSender={showSender}
        handleMarkAsUnread={handleMarkAsUnread}
        participantsWithoutUser={participantsWithoutUser}
        isDM={currentThread.type === "private"}
      />
    );
  }

  const getMessage = () => {
    if (comments) {
      if (!openCommentsModal.comments.length) return null;
      return generateItems(openCommentsModal.comments).map((m, i, arr) => renderItem(m, arr[i - 1]));
    }
    if (!chatMessages.length) return null;
    return generateItems(chatMessages, unreadCountFromRedux).map((m, i, arr) => renderItem(m, arr[i - 1]));
  };

  const location = useLocation();
  const { taskId } = useParams();
  const screen = location.pathname.includes("schedule") && location.pathname.includes("task")
    ? "schedule" : location.pathname.includes("library") ? "library" : "messages";
  const { hasPermission: COMMENT_ADD } = usePermission("14");
  const { hasPermission: LIB_COMMENT_ADD } = usePermission("26");
  const commentsAddPermissions = screen === "schedule" ? COMMENT_ADD : screen === "library" ? LIB_COMMENT_ADD : true;

  if (view === "quickChat") {
    return (
      <div className="flex h-full flex-col justify-between overflow-hidden">
        <div className="relative grid flex-1 min-h-0 h-full max-h-full overflow-x-hidden overflow-y-scroll">
          <div className="flex flex-col last:mb-3 justify-end">
            {getMessage()}
            <div ref={messagesEndRef} />
          </div>
        </div>
        <div className="mx-5 mb-5">
          <ConversationInput socket={socket} comments={comments} allowRequest={false} />
        </div>
      </div>
    );
  }

  if (!chatIdStr && !comments) return <ChatEmptyState />;

  return (
    <div className="flex h-full min-h-0 w-full">
      <div className={`flex flex-1 min-w-0 ${taskId ? "flex-col-reverse" : "flex-col"} justify-between h-full min-h-0 overflow-y-hidden`} id="conversations-tour-chat-window">

        {!comments && (
          <>
            <ChatHeader isGroup={currentThread.type === "group"} memberList={[]} />
            <RequestHeader socket={socket} />
          </>
        )}
        <div
          className={`flex ${taskId ? "" : "flex-col"} justify-end h-full min-h-0 bg-repeat`}
          style={{
            backgroundImage: "url(/images/v3/conversations/default-wallpaper.svg)",
          }}
        >
          {!comments && <PinnedMessageBanner pinnedMessage={pinnedMessage} onClick={handleBannerPress} />}
          <div className="relative flex-1 min-h-0 w-full">
            <DragAndDropLayer comments={comments} containerRef={chatWindow}>
              <div
                className="absolute inset-0 overflow-x-hidden overflow-y-scroll"
                ref={chatWindow}
                onScroll={handleScroll}
              >
                {chatPagination?.loading && (
                  <div className="flex items-center justify-center w-full my-2">
                    <svg className="animate-spin" width={18} height={18} viewBox="0 0 50 50">
                      <circle cx="25" cy="25" r="20" fill="none" stroke="#165ABF" strokeWidth="4" strokeDasharray="90,150" strokeLinecap="round" />
                    </svg>
                  </div>
                )}
                <div className="flex flex-col last:mb-3 justify-end min-h-full">
                  {getMessage()}
                  {/* Bottom sentinel — scrollToChatBottom() targets this instead
                    of doing manual scrollTop/scrollHeight math, so it stays
                    correct even when images/attachments load late or the
                    composer below changes height. */}
                  <div ref={messagesEndRef} />
                </div>
              </div>
            </DragAndDropLayer>
          </div>
          <TypingIndicator typingStatus={typingStatusState} currentThread={currentThread} />
          {/* {pinActionToast.visible && (
            <div className="flex justify-center flex-shrink-0 mb-1">
              <div className="px-[10px] py-1 border border-[#DDE5ED] rounded-[12px] bg-[#F9FBFE] out-500-12 text-gray-700 shadow-sm">
                {pinActionToast.action === "pinned" ? "You pinned a message" : "You un-pinned a message"}
              </div>
            </div>
          )} */}
          {commentsAddPermissions && (
            <div className={`${taskId ? "" : "mx-5"} mb-5 flex-shrink-0`}>
              <ConversationInput socket={socket} comments={comments} />
            </div>
          )}
        </div>
        <DeleteMessageModal
          visible={deleteMessageModal.visible}
          onClose={() => setDeleteMessageModal({ visible: false, id: null })}
          onDelete={() => {
            deleteMessage(deleteMessageModal.id);
            setDeleteMessageModal({ visible: false, id: null });
          }}
        />
      </div>
      <MessageInfoDrawer />
    </div >
  );
};

export default ChatWindow;